import React, {useReducer, useRef} from 'react';
import {ContentAdDesktop} from 'app/styles/content';
import {useDispatch} from 'react-redux';
import {Subheading} from 'app/styles/heading';
import {LOADING_MESSAGE, LoadingIndicator} from '@fupa/fupa-uikit';
import TopElevenTeam from 'app/components/topeleven/TopElevenTeam';
import TopElevenInformation from 'app/components/topeleven/TopElevenInformation';
import TopElevenPlaceholder from 'app/components/topeleven/TopElevenPlaceholder';
import {Field, FieldLine, FuPaPlayer, Goal, MainContainer, MidLine} from 'app/components/topeleven/TopElevenStyles';
import {Helmet} from 'react-helmet-async';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import {fetchLeagueTopEleven} from 'app/routes/league/LeagueTopElevenPageActions';
import {LeagueTopElevenPageReducer} from 'app/routes/league/LeagueTopElevenPageReducer';
import {useLocation, useParams} from 'react-router';
import {SponsoringBannerBottom, SponsoringBannerTop} from 'app/components/topeleven/TopElevenPageSponsoring';
import {useTopelevenNRDTrigger} from 'app/hooks/nativeRatingDialogTrigger/useTopelevenNRD';
import {useFetchData, useRestoreData} from 'app/hooks/dataHooks';
import {ShareButtonInContent} from 'app/components/socialShare/ShareButtonInContent';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex: 100;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  margin-bottom: 1rem;
  padding-right: 1rem;
`;

const SCSubheading = styled(Subheading)`
  &&& {
    padding-top: 0;
    margin-bottom: 0;
  }
`;
const adSlotEnabledAgeGroupSlugs = ['m', 'mr', 'w'];

const LeagueTopElevenPage = () => {
  const {pathname} = useLocation();
  const {restoredData} = useRestoreData('LeagueTopElevenPage');
  const {leagueSlug, seasonSlug, number} = useParams();
  const initialState = restoredData ?? {
    metaLeague: {},
    topelevenContent: {},
    isFetching: false,
  };
  const [state, dispatch] = useReducer(LeagueTopElevenPageReducer, initialState);
  const dispatchRedux = useDispatch();

  const loadData = () => fetchLeagueTopEleven(leagueSlug, number, seasonSlug, dispatchRedux)(dispatch, state);
  const {topelevenContent, isFetching, metaLeague} = state;
  useFetchData(state, loadData, 'LeagueTopElevenPage');

  const adsAllowed = adSlotEnabledAgeGroupSlugs.includes(metaLeague?.ageGroup?.slug);
  const ratingTriggerRef = useRef(null);
  const contentLoaded = topelevenContent.status && !isFetching;
  useTopelevenNRDTrigger(contentLoaded, ratingTriggerRef);

  const headerParentName =
    seasonSlug && metaLeague.season ? `${metaLeague.name} ${metaLeague.season.name}` : metaLeague.name;

  const districtName = metaLeague?.district?.name ?? '';
  const title = `Elf der Woche - ${number}. Spieltag - ${headerParentName} - ${districtName}${TITLE_POSTFIX}`;
  const description = `Die Elf der Woche vom ${number}. Spieltag der Liga ${headerParentName} - Kreis ${districtName}.`;
  const meta = [
    {
      name: 'description',
      content: description,
    },
    {
      name: 'robots',
      content: `noindex, follow`,
    },
    {property: 'og:description', content: description},
    {property: 'og:image', content: `https://static.fupa.net/social/fupa-topeleven.jpg`},
    {property: 'og:image:secure_url', content: `https://static.fupa.net/social/fupa-topeleven.jpg`},
    {property: 'og:image:type', content: 'image/jpg'},
    {property: 'og:image:width', content: '2400px'},
    {property: 'og:image:height', content: '1260px'},
    {property: 'og:title', content: title},
    {property: 'og:site_name', content: 'FuPa'},
    {property: 'og:url', content: `https://www.fupa.net${pathname}`},
    {property: 'fb:app_id', content: '939336572887264'},
  ];

  const topeleven = topelevenContent.status ? (
    <>
      <FuPaPlayer>
        <svg>
          <path
            d='M234.625 2.694c1.36 4.25-6.119 4.46-7.107 7.123 3.534.5 6.807-1.205 9.463-.202.245 2.379-1.937 2.207-3.644 2.648-.91.24-6.854 1.601-7.977 2.648 1.629.756 2.988 1.938 4.103 2.85 1.123.951 2.885 1.37 3.225 2.851-3.66 1.227-6.466-1.713-8.83-3.052-3.716 1.578-8.862 1.795-13.344 2.648-6.325 9.591-15.977 17.275-25.4 24.442-2.515 1.915-15.041 8.95-16.527 9.81-2.27 1.317-6.678 4-8.014 4.719-4.878 2.633-9.352 4.802-14.372 7.675-1.542.883-6.943 7.038-7.417 7.8-.553.891-3.333 21.924-5.333 30.7-1.786 7.885-4.182 16.42-6.237 23.625-.783 2.753-5.091 15.09-5.597 16.497-.57 1.608-6.325 12.972-6.024 14.663.245 1.377-.79 9.584-.862 10.594-1.66 1.473-4.237 12.98-4.949 15.276-1.146 3.681-2.166 7.833-4.727 9.367-.775.471-3.66 1.728-4.309 2.244-1.138.908-2.07 2.267-2.798 4.078-1.731 3.98-10.973 18.568-12.056 20.364-.356.419-4.356 4.444-5.81 6.314-.791 1.018-9.954 12.187-11.622 13.452-5.186 3.905-10.632 7.174-15.075 11.61.838 2.238 2.087 4.175 4.53 4.886.814.24 7.636 2.79 9.47 4.272 1.17.957 4.957 3.95 4.736 5.499-.293 1.982-3.827 2.02-6.46 1.832-4.924-.336-10.624-1.331-16.577-1.623-7.431-.36-15.044 1.01-18.728-2.85-.063-3.33 3.4-7.736 4.309-7.744 1.723-4.503 5.423-7.833 8.395-11.61 2.973-3.764 5.676-7.639 7.969-12.016 2.26-4.339 4.277-9.007 6.886-13.04 2.648-4.084 7.075-6.47 10.11-10.182 0-.478 4.222-4.22 4.523-4.683 1.328-5.26 4.332-8.94 4.087-15.688-2.032-.883-3.518-3.127-4.95-4.676-1.17-1.272-.71-8.589.436-10.594 1.628-2.858 2.016-6.21 3.439-8.97-1.55 1.07-7.44 7.122-8.175 8.559-1.383 2.686-6.98 7.204-8.83 6.718-2.11-.546-4.293-.232-7.313-.404-1.636.898-2.49 2.544-4.743 2.85-3.154 2.91-5.874 5.589-8.601 8.56-2.11 2.28-8.799 7.495-9.906 8.146-2.877 1.684-10.198 1.586-10.98 1.422-3.408-.718-5.93-4.033-6.23-7.332-.134-1.332 1.336-11.185 2.364-13.848.506-1.332 2.19-7.167 2.79-8.559 2.973-6.89 7.353-11.064 12.056-16.496 1.052-1.212 2.388-2.32 2.586-3.262.403-1.997-2.475-8.17-2.799-9.164-1.297-3.98.126-9.03 4.514-8.552.538-.65 16.388.39 17.226.404 1.304-.95 1.557-2.566 2.15-3.867 1.898-4.182 4.878-7.542 7.32-10.998-.614-.636 2.894-3.773 5.46-17.449 2.157-11.502-.84-29.211-.511-30.436-2.42.606-7.66 3.715-9.47 4.291-1.407.457-5.724 2.454-7.1 2.245-1.098 1.788-16.071 6.853-17.866 5.7-2.53.651-4.498 1.235-6.458 2.043-3.02 1.242-6.024 2.484-7.97 4.474 1.961-.24 2.602.456 2.586 1.429-.008 1.518-1.779 1.384-3.012 1.833 1.605.007 1.921 1.271 1.502 2.648-.348.68-1.439.673-2.142 1.017-.696 2.582 1.747 5.29-.435 7.13-2.941.202-2.38-2.91-3.66-4.28-.862-.508-2.19.03-2.799.412.293 1.646.94 4.796-1.075 4.482-.617-.48-.727-1.22-1.075-1.833-1.012-1.818-2.356-2.619-3.447-4.078.316-1.331-1.257-2.281-1.502-3.665-.11-.606.427-1.197.427-2.245 0-.785.419-5.297.862-5.903.506-.688 2.008-1.279 3.446-2.035 3.755-1.99 6.823-4.638 9.471-6.927 3.083-2.671 12.538-7.669 13.558-8.35 1.668-1.114 2.98-2.925 4.949-4.077 2.103-1.227 8.119-4.376 9.044-5.087.87-.68 7.107-5.993 7.747-6.726 7.247-5.875 11.408-8.997 12.483-9.366 2.047-.711 4.387-.075 6.458-.614 1.44-.374 2.61-1.519 3.874-2.035 1.518-.636 6.696-1.878 6.886-2.858-1.004-3.329-3.882-4.877-3.874-9.157-1.834-3.526-2.838-6.04-3.012-7.54-.182-1.497.174-3.128 0-4.684-.545-4.81.411-10.586 2.799-13.444 1.059-.77 5.644-3.082 6.672-3.464 3.89-1.451 13.218-.621 15.921 1.017 3.787 2.297 5.273 3.838 6.467 8.357.988 3.763 1.66 6.666.427 10.586.94-.082 1.455.247 1.502 1.018.245 2.244.119 4.361-.648 6.314-.277.726-.933 1.272-1.075 2.035-.317 1.728.316 3.262.434 5.304 3.36-.464 3.076 1.953 4.736 3.052.798.532 6.253 2.327 7.107 2.44 2.387.336 5.684-1.302 8.609-1.624 1.462-.165 3.225.082 4.94 0 3.874-.195 16.737-.98 17.654-1.025 1.407-.06 27.526-8.529 29.7-9.569 1.13-.538 11.17-6.374 14.641-8.558 3.186-2.005 12.578-9.195 13.558-9.569 1.186-.464 2.656-.27 3.874-.613 2.016-.569 3.376-1.616 5.17-2.447.648-.464.221-1.952 1.289-2.035.972-.718 1.55.344 2.15.614 1.589-2.484 3.842-3.681 6.245-5.297 1.162-.778 2.356-2.147 4.087-1.833 1.408 2.933-2.545 3.845-3.439 5.903 3.518-1.025 6.262-3.225 9.471-3.255z'
            fill='#FFF'
            fillRule='evenodd'
          />
        </svg>
      </FuPaPlayer>
      <MidLine />
      <TopElevenTeam topeleven={topelevenContent} adsAllowed={adsAllowed} />
    </>
  ) : (
    <TopElevenPlaceholder />
  );

  const content = isFetching ? (
    <LoadingIndicator message={LOADING_MESSAGE.loadingData} bgColor={false} />
  ) : (
    <>
      <Wrapper>
        <SCSubheading variant='subheading2'>{topelevenContent.matchDay}. Spieltag</SCSubheading>
        <ShareButtonInContent
          eventCat='topeleven'
          eventSlug={`${leagueSlug}_${seasonSlug ?? 'current'}_${number}`}
          text={title}
          label={'Elf der Woche teilen'}
        />
      </Wrapper>
      <MainContainer>
        {adsAllowed && <SponsoringBannerTop />}
        <Field>
          <FieldLine>
            <Goal>
              <svg width='180' height='107' xmlns='http://www.w3.org/2000/svg'>
                <g fillRule='nonzero' stroke='#FFF' strokeWidth='4' fill='none'>
                  <path d='M47 0v28h86V0M60 75c0 16.569 13.431 30 30 30 16.569 0 30-13.431 30-30' />
                  <path d='M2 0v73h176V0' />
                </g>
              </svg>
            </Goal>
            {topeleven}
          </FieldLine>
        </Field>
        {adsAllowed && <SponsoringBannerBottom />}
        <div ref={ratingTriggerRef} />
        <TopElevenInformation topeleven={topelevenContent} />
      </MainContainer>
    </>
  );

  return (
    <ContentAdDesktop noTabs top='4.5rem'>
      <Helmet title={title} meta={meta} />
      {content}
    </ContentAdDesktop>
  );
};

export default LeagueTopElevenPage;
